import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// =================== ref.individuals ===================

export interface Individual {
  comment?: string | null;
  id: string;
  last_name: string;
  first_name: string;
  middle_name: string | null;
  full_name: string | null;
  inn: string | null;
  birth_date: string | null;
  birth_place: string | null;
  gender: 'M' | 'F' | null;
  pf_insurance_number: string | null;
  citizenship_country: string | null;
  is_non_resident: boolean;
  passport_doc_type: string | null;
  passport_series: string | null;
  passport_number: string | null;
  passport_issued_by: string | null;
  passport_issued_at: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  // Реквізити для виплат (alembic 0314).
  payout_iban: string | null;
  payout_bank_name: string | null;
  // домен 3 плоско: інвалідність + сімейний стан
  has_disability: boolean;
  disability_group: string | null;
  disability_cert_series: string | null;
  disability_cert_number: string | null;
  disability_cert_issued_at: string | null;
  disability_from: string | null;
  disability_to: string | null;
  /** ч.1208: ІІ група з порушенням зору / психічними розладами — як 2 робочих місця у нормативі. */
  disability_double_count?: boolean;
  marital_status: string | null;
  // домен 4 плоско: пенсіонер
  is_pensioner: boolean;
  pension_cert_series: string | null;
  pension_cert_number: string | null;
  pension_cert_issued_at: string | null;
  military: IndividualMilitary | null;
  family: IndividualFamilyMember[];
  stazh: IndividualStazhRow[];
  needs_review: boolean;
  deletion_mark: boolean;
  external_source?: string | null;  // значок «І» імпорту з 1С
}

export interface IndividualMilitary {
  rank: string | null;
  vus: string | null;
  fitness: string | null;
  military_office: string | null;
  obligation_relation: string | null;
  registration_relation: string | null;
  military_id_details: string | null;
  registry_record_number: string | null;
}

export interface IndividualFamilyMember {
  id?: string;
  line_number: number;
  kinship_degree: string | null;
  member_full_name: string | null;
  member_individual_id: string | null;
  birth_year: number | null;
  is_dependent: boolean;
}

export interface IndividualStazhRow {
  id?: string;
  line_number: number;
  stazh_kind: string | null;
  stazh_date: string | null;
  work_place: string | null;
  days_count: number | null;
}

export interface IndividualCreate {
  comment?: string | null;
  last_name: string;
  first_name: string;
  middle_name?: string | null;
  inn?: string | null;
  birth_date?: string | null;
  birth_place?: string | null;
  gender?: 'M' | 'F' | null;
  pf_insurance_number?: string | null;
  citizenship_country?: string | null;
  is_non_resident?: boolean;
  passport_doc_type?: string | null;
  passport_series?: string | null;
  passport_number?: string | null;
  passport_issued_by?: string | null;
  passport_issued_at?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  payout_iban?: string | null;
  payout_bank_name?: string | null;
  // домен 3 плоско
  has_disability?: boolean;
  disability_group?: string | null;
  disability_cert_series?: string | null;
  disability_cert_number?: string | null;
  disability_cert_issued_at?: string | null;
  disability_from?: string | null;
  disability_to?: string | null;
  disability_double_count?: boolean;
  marital_status?: string | null;
  // домен 4 плоско
  is_pensioner?: boolean;
  pension_cert_series?: string | null;
  pension_cert_number?: string | null;
  pension_cert_issued_at?: string | null;
}

/** ч.811: рахунок фізособи (кілька на картці; основний = дзеркало payout_iban). */
export interface IndividualBankAccount {
  id: string;
  individual_id: string;
  iban: string;
  account_name: string | null;
  is_main: boolean;
  bank_name: string | null;
}
export interface EmployeeBankAccounts {
  employee_id: string;
  accounts: IndividualBankAccount[];
}

export const individualBankAccounts = {
  list: (indId: string) =>
    api.get<IndividualBankAccount[]>(`/individuals/${indId}/bank-accounts`).then((r) => r.data),
  add: (indId: string, p: { iban: string; account_name?: string | null; is_main?: boolean }) =>
    api.post<IndividualBankAccount[]>(`/individuals/${indId}/bank-accounts`, p).then((r) => r.data),
  update: (indId: string, accId: string, p: { iban: string; account_name?: string | null; is_main?: boolean }) =>
    api.patch<IndividualBankAccount[]>(`/individuals/${indId}/bank-accounts/${accId}`, p).then((r) => r.data),
  remove: (indId: string, accId: string) =>
    api.delete<IndividualBankAccount[]>(`/individuals/${indId}/bank-accounts/${accId}`).then((r) => r.data),
  byEmployees: (employeeIds: string[]) =>
    api.get<EmployeeBankAccounts[]>('/individuals/accounts-by-employees',
      { params: { employee_ids: employeeIds.join(',') } }).then((r) => r.data),
};

export const individuals = {
  list: (params?: { q?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<Individual>>('/individuals', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<Individual>(`/individuals/${id}`).then((r) => r.data),
  create: (p: IndividualCreate) =>
    api.post<Individual>('/individuals', p).then((r) => r.data),
  update: (id: string, p: Partial<IndividualCreate>) =>
    api.patch<Individual>(`/individuals/${id}`, p).then((r) => r.data),
  upsertMilitary: (id: string, p: IndividualMilitary) =>
    api.put<IndividualMilitary>(`/individuals/${id}/military`, p).then((r) => r.data),
  replaceFamily: (id: string, items: Omit<IndividualFamilyMember, 'id'>[]) =>
    api.put<IndividualFamilyMember[]>(`/individuals/${id}/family`, { items }).then((r) => r.data),
  replaceStazh: (id: string, items: Omit<IndividualStazhRow, 'id'>[]) =>
    api.put<IndividualStazhRow[]>(`/individuals/${id}/stazh`, { items }).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/individuals/${id}`).then(() => undefined),
};

// =================== ref.positions ===================

export interface Position {
  id: string;
  code: string;
  name: string;
  kp_code: string | null;
  kp_professional_name: string | null;
  deletion_mark: boolean;
  external_source?: string | null;  // значок «І» імпорту з 1С
}

export interface PositionCreate {
  code: string;
  name: string;
  kp_code?: string | null;
  kp_professional_name?: string | null;
}

export const positionsApi = {
  list: (params?: { sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<Position>>('/positions', { params }).then((r) => r.data),
  create: (p: PositionCreate) =>
    api.post<Position>('/positions', p).then((r) => r.data),
  update: (id: string, p: Partial<PositionCreate>) =>
    api.patch<Position>(`/positions/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/positions/${id}`).then(() => undefined),
};

// =================== довідники графік/вид розрахунку (0282) ===================

export interface RefOption {
  id: string;
  code: string;
  name: string;
}

export interface WorkSchedule {
  id: string;
  code: string;
  name: string;
  schedule_type: 'FIVE_DAY';
  hours_per_week: number | string;
  works_mon: boolean;
  works_tue: boolean;
  works_wed: boolean;
  works_thu: boolean;
  works_fri: boolean;
  works_sat: boolean;
  works_sun: boolean;
  is_active: boolean;
  is_system: boolean;
  sort_order: number;
  external_source?: string | null;
}

export type WorkScheduleInput = Omit<
  WorkSchedule,
  'id' | 'is_system' | 'external_source'
>;

export const workSchedulesApi = {
  // hire-order dropdown сумісний: WorkSchedule має id/code/name (RefOption).
  list: (onlyActive = true) =>
    api
      .get<WorkSchedule[]>('/work-schedules', {
        params: { only_active: onlyActive },
      })
      .then((r) => r.data),
  create: (body: Partial<WorkScheduleInput>) =>
    api.post<WorkSchedule>('/work-schedules', body).then((r) => r.data),
  update: (id: string, body: Partial<WorkScheduleInput>) =>
    api.patch<WorkSchedule>(`/work-schedules/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/work-schedules/${id}`).then((r) => r.data),
};
export const calcTypesApi = {
  list: () => api.get<RefOption[]>('/calc-types').then((r) => r.data),
};

export const LABOR_CONTRACT_TYPE_LABELS: Record<string, string> = {
  PERMANENT: 'Безстроковий',
  FIXED_TERM: 'Строковий',
  CONTRACT: 'Контракт',
  PART_TIME: 'Сумісництво',
};

// =================== ref.profession_classifier ===================

export interface ProfessionClassifierEntry {
  id: string;
  kp_code: string;
  professional_name: string;
  name: string | null;             // назва групи
  class_code: string | null;
  class_name: string | null;
  zkpptr_code: string | null;
  etkd_issue: string | null;
  dkhp_issue: string | null;
  deletion_mark: boolean;
}

export const professionClassifier = {
  list: (params?: { q?: string; limit?: number; offset?: number }) =>
    api.get<Page<ProfessionClassifierEntry>>('/profession-classifier', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<ProfessionClassifierEntry>(`/profession-classifier/${id}`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/profession-classifier/${id}`).then(() => undefined),
};

// =================== ref.departments ===================

export interface Department {
  id: string;
  organization_id: string;
  parent_id: string | null;
  code: string;
  name: string;
  deletion_mark: boolean;
  default_direction_id?: string | null;
  external_source?: string | null;  // значок «І» імпорту з 1С
}

export const departmentsApi = {
  list: (params?: { organization_id?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<Department>>('/departments', { params }).then((r) => r.data),
  create: (p: {
    organization_id: string; parent_id?: string | null; code: string; name: string;
    default_direction_id?: string | null;
  }) =>
    api.post<Department>('/departments', p).then((r) => r.data),
  update: (id: string, p: Partial<{
    parent_id: string | null; code: string; name: string;
    default_direction_id: string | null;
  }>) =>
    api.patch<Department>(`/departments/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/departments/${id}`).then(() => undefined),
};

// =================== ref.employees (extended) ===================

// Block 9 (2026-05-27): ПСП categories per ПКУ ст.169
export type PspCategory =
  | 'GENERAL'
  | 'MULTIPLE_CHILDREN'
  | 'SINGLE_PARENT'
  | 'DISABLED_CHILD'
  | 'DISABILITY_GROUP_1_2'
  | 'DISABILITY_GROUP_3'
  | 'CHERNOBYL_VICTIM_1_2'
  | 'CHERNOBYL_VICTIM_3_4'
  | 'COMBAT_VETERAN'
  // TD-B-P5.4.2 (2026-06-01): 3 нові категорії за ПКУ ст.169.
  | 'HERO_OF_UKRAINE'
  | 'STUDENT'
  | 'WIDOW_WITH_CHILDREN';

// Вид зайнятості (alembic 0270, SPEC Employment Types).
export type EmploymentType = 'STAFF' | 'CIVIL_LAW' | 'NO_CONTRACT';

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  STAFF: 'Штат (трудові відносини)',
  CIVIL_LAW: 'Цивільно-правовий (ЦПУ)',
  NO_CONTRACT: 'Без договору (інформаційно)',
};

// Вид зайнятості (alembic 0292, дзеркало 1С ВидыЗанятости). is_main_workplace —
// похідне (=MAIN).
export type EmploymentKind = 'MAIN' | 'EXTERNAL' | 'INTERNAL';

export const EMPLOYMENT_KIND_LABELS: Record<EmploymentKind, string> = {
  MAIN: 'Основне місце роботи',
  EXTERNAL: 'Сумісництво (зовнішнє)',
  INTERNAL: 'Внутрішнє сумісництво',
};

export const PSP_CHILDREN_CATEGORIES: PspCategory[] = [
  'MULTIPLE_CHILDREN', 'SINGLE_PARENT', 'DISABLED_CHILD',
  // TD-B-P5.4.2: WIDOW_WITH_CHILDREN — multiplier 1.5 × N.
  'WIDOW_WITH_CHILDREN',
];

export const PSP_CATEGORY_LABELS: Record<PspCategory, string> = {
  GENERAL: 'Загальна ПСП (100%)',
  MULTIPLE_CHILDREN: 'На дітей (100% × N)',
  SINGLE_PARENT: 'Одинокий батько/мати (150% × N)',
  DISABLED_CHILD: 'На дитину-інваліда (150% × N)',
  DISABILITY_GROUP_1_2: 'Iнвалід I/II групи (150%)',
  DISABILITY_GROUP_3: 'Iнвалід III групи (100%)',
  CHERNOBYL_VICTIM_1_2: 'Чорнобилець 1-2 категорії (150%)',
  CHERNOBYL_VICTIM_3_4: 'Чорнобилець 3-4 категорії (100%)',
  COMBAT_VETERAN: 'Учасник бойових дій (150%)',
  // TD-B-P5.4.2 (2026-06-01).
  HERO_OF_UKRAINE: 'Герой України (200%)',
  STUDENT: 'Учень/студент (150%)',
  WIDOW_WITH_CHILDREN: 'Вдова/вдівець з дітьми (150% × N)',
};

export interface EmployeeFull {
  comment?: string | null;
  id: string;
  organization_id: string;
  individual_id: string;
  code: string | null;
  position_id: string | null;
  department_id: string | null;
  activity_place_id: string | null;  // ч.806: місце діяльності (відокремлений підрозділ)
  work_schedule_id: string | null;
  hire_date: string | null;
  dismiss_date: string | null;
  is_main_workplace: boolean;
  employment_kind: EmploymentKind;
  employment_type: EmploymentType;
  /** ч.1208: шкідливі умови праці — поза нормативом осіб з інвалідністю. */
  excluded_from_disability_norm?: boolean;
  /** ч.1209: чинна сьогодні тривала відсутність за проведеним наказом. */
  absence_order_id?: string | null;
  absence_kind?: string | null;
  absence_from?: string | null;
  absence_to?: string | null;
  dismiss_reason: string | null;
  deletion_mark: boolean;
  full_name: string | null;
  inn: string | null;
  position_name: string | null;
  department_name: string | null;
  salary_amount: string | null;
  // Block 9 ПСП
  psp_category: PspCategory | null;
  children_count: number | null;
  // Фіча картки #2: персональні рахунки обліку оплат (override картки).
  expense_account_id: string | null;
  payroll_account_id: string | null;
  // Реквізити для виплат з ФIЗОСОБИ (read-only, alembic 0314). Спосіб виводиться:
  // є IBAN → BANK, інакше → CASH.
  individual_payout_iban?: string | null;
  individual_payout_bank_name?: string | null;
  // Джерело: '1c' (імпорт) → бейдж «І».
  external_source: string | null;
  // Похідний прапор «чинний» (backend, та сама логіка що фільтр active_only).
  is_active?: boolean;
  // Наказ прийому (для блокування картки): id + чи проведений.
  hire_order_id?: string | null;
  hire_order_posted?: boolean;
  // V1: норма днів відпустки + стартовий залишок.
  vacation_days_per_year?: number | null;
  vacation_additional_days_per_year?: number | null;
  vacation_balance_start_days?: number | null;
  vacation_balance_start_date?: string | null;
  // alembic 0322: стартовий залишок обчислено з імпорту 1С (жовтий трикутник).
  vacation_balance_is_estimated?: boolean;
}

export interface EmployeePlannedAccrualRow {
  id: string;
  type_code: string;
  type_name: string;
  value_kind: 'FIXED_SUM' | 'PERCENT' | 'RATE';
  value: string;
  valid_from: string;
  valid_to: string | null;
  source_doc_type: string;
  source_doc_id?: string;
  is_current?: boolean;
  is_draft?: boolean;       // з непроведеного наказу (ТЧ), ще не у регістрі
  doc_number?: string | null;
}

// Рядок ТЧ «Нарахування» наказу (оклад/надбавки) — для форми наказа.
export interface PlannedAccrualLine {
  id: string;
  line_number: number;
  accrual_type_id: string;
  value_kind: 'FIXED_SUM' | 'PERCENT' | 'RATE' | null;
  value: string | null;
  percent_base_accrual_type_id: string | null;
  rate_quantity: string | null;
  action: string | null;
}

export interface EmployeeState {
  id: string;
  employee_id: string;
  valid_from: string;
  position_id: string | null;
  department_id: string | null;
  salary_amount: string;
  is_active: boolean;
  source_doc_type: string;
  source_doc_id: string;
}

export const employees = {
  list: (params?: {
    organization_id?: string;
    active_only?: boolean;
    employment_type?: string;
    q?: string;
    limit?: number;
    inactive_only?: boolean; sort_by?: string; sort_dir?: 'asc' | 'desc'; offset?: number;
  }) =>
    api.get<Page<EmployeeFull>>('/employees', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<EmployeeFull>(`/employees/${id}`).then((r) => r.data),
  create: (p: {
    organization_id: string;
    individual_id: string;
    code?: string | null;
    employment_kind?: EmploymentKind;
    employment_type?: EmploymentType;
    psp_category?: PspCategory | null;
    children_count?: number | null;
    activity_place_id?: string | null;
  }) =>
    api.post<EmployeeFull>('/employees', p).then((r) => r.data),
  update: (id: string, p: Partial<{
    comment: string | null;  // ч.883
    code: string;
    employment_kind: EmploymentKind;
    employment_type: EmploymentType;
    psp_category: PspCategory | null;
    children_count: number | null;
    expense_account_id: string | null;
    payroll_account_id: string | null;
    work_schedule_id: string | null;
    activity_place_id: string | null;
    excluded_from_disability_norm: boolean;
    vacation_days_per_year: number | null;
    vacation_additional_days_per_year: number | null;
    vacation_balance_start_days: number | null;
    vacation_balance_start_date: string | null;
  }>) =>
    api.patch<EmployeeFull>(`/employees/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/employees/${id}`).then(() => undefined),
  history: (id: string) =>
    api.get<EmployeeState[]>(`/employees/${id}/history`).then((r) => r.data),
  stateAt: (id: string, at: string) =>
    api.get<EmployeeState | null>(`/employees/${id}/state-at`, { params: { at } }).then((r) => r.data),
  // Фіча #3: планові нарахування (read-only картка). history=true → вся історія
  // змін (включно з закритими записами по всіх наказах); інакше — активні на сьогодні.
  plannedAccruals: (id: string, history = false) =>
    api.get<EmployeePlannedAccrualRow[]>(`/employees/${id}/planned-accruals`, {
      params: history ? { history: true } : undefined,
    }).then((r) => r.data),
  // Composite: фізособа (нова/існуюча) + працівник + наказ/договір атомарно.
  createWithDocument: (p: EmployeeWithDocumentCreate) =>
    api
      .post<EmployeeWithDocumentOut>('/employees/with-document', p)
      .then((r) => r.data),
};

export interface NewIndividual {
  last_name: string;
  first_name: string;
  middle_name?: string | null;
  inn: string;
  birth_date: string;
}

export interface EmployeeWithDocumentCreate {
  organization_id: string;
  employment_type: EmploymentType;
  individual_id?: string;
  individual?: NewIndividual;
  code?: string | null;
  employment_kind?: EmploymentKind;
  psp_category?: PspCategory | null;
  children_count?: number | null;
  activity_place_id?: string | null;  // ч.806
  labor_contract_type?: string | null;
  hire_order?: {
    document_date: string;
    hire_date: string;
    position_id?: string | null;
    department_id?: string | null;
    salary_amount?: number;
    probation_months?: number | null;
    probation_days?: number | null;
    contract_basis?: string | null;
    work_schedule_id?: string | null;
    calc_type_id?: string | null;
    contract_valid_until?: string | null;
    comment?: string | null;
  };
  civil_law?: {
    document_date: string;
    contract_subject?: string | null;
    date_from: string;
    date_to?: string | null;
    total_amount?: number;
    calc_type?: 'ONCE' | 'DAILY' | 'HOURLY';
    income_sign?: string;
    department_id?: string | null;
    payment_term?: string | null;
    comment?: string | null;
  };
  post?: boolean;
}

export interface EmployeeWithDocumentOut {
  individual_id: string;
  employee_id: string;
  document_type: string | null;
  document_id: string | null;
  posted: boolean;
}

// =================== Documents ===================

export interface HireOrder {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  hire_date: string;
  position_id: string | null;
  department_id: string | null;
  employment_kind: EmploymentKind | null;
  salary_amount: string;
  probation_months: number | null;
  probation_days: number | null;
  contract_basis: string | null;
  work_schedule_id: string | null;
  calc_type_id: string | null;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  external_source: string | null;
  planned_accruals?: PlannedAccrualLine[];
}

export interface HireOrderCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  hire_date: string;
  position_id?: string | null;
  department_id?: string | null;
  employment_kind?: EmploymentKind | null;
  salary_amount: string;
  probation_months?: number | null;
  probation_days?: number | null;
  contract_basis?: string | null;
  work_schedule_id?: string | null;
  calc_type_id?: string | null;
  comment?: string | null;
}

// Backfill керівника/головбуха у картку організації з наявних наказів.
export function resolveOrgOfficials(organizationId: string) {
  // ч.1179: null = знято (переведений/звільнений працівник).
  return api.post<{ updated: Record<string, string | null>; found_orders: number }>(
    '/hire-orders/resolve-org-officials', { organization_id: organizationId },
  ).then((r) => r.data);
}

export interface HrOrderRegistryRow {
  id: string;
  kind: 'hire' | 'transfer' | 'dismissal' | 'accrual' | 'vacation' | 'absence';
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  pib: string;
}

export const hrOrdersRegistry = (params?: {
  organization_id?: string; kind?: HrOrderRegistryRow['kind']; posted?: boolean; q?: string;
  date_from?: string; date_to?: string;
  sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
}) => api.get<Page<HrOrderRegistryRow>>('/hire-orders/registry', { params }).then((r) => r.data);

export const hireOrders = {
  list: (params?: { organization_id?: string; employee_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<HireOrder>>('/hire-orders', { params }).then((r) => r.data),
  get: (id: string) => api.get<HireOrder>(`/hire-orders/${id}`).then((r) => r.data),
  create: (p: HireOrderCreate) =>
    api.post<HireOrder>('/hire-orders', p).then((r) => r.data),
  update: (id: string, p: Partial<HireOrderCreate>) =>
    api.patch<HireOrder>(`/hire-orders/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`/hire-orders/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<HireOrder>(`/hire-orders/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<HireOrder>(`/hire-orders/${id}/unpost`).then((r) => r.data),
};

// Договір ЦПУ (Employment Types Фаза 2). Підстава для працівника виду CIVIL_LAW.
export interface CivilLawContract {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  contract_subject: string | null;
  date_from: string;
  date_to: string | null;
  total_amount: string;   // «Розмір, грн» (ONCE — вся сума; DAILY/HOURLY — за день/годину)
  calc_type: 'ONCE' | 'DAILY' | 'HOURLY';
  income_sign: string;
  state: string;
  posted: boolean;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  posted_at: string | null;
  deletion_mark: boolean;
  department_id: string | null;
  comment: string | null;
  external_source: string | null;
  employee_name: string | null;
}

export interface CivilLawContractCreate {
  organization_id: string;
  document_number?: string;
  document_date: string;
  employee_id: string;
  contract_subject?: string | null;
  date_from: string;
  date_to?: string | null;
  total_amount: string;   // «Розмір, грн»
  calc_type?: 'ONCE' | 'DAILY' | 'HOURLY';
  income_sign?: string;
  department_id?: string | null;
  comment?: string | null;
}

export const civilLawContracts = {
  list: (params?: { organization_id?: string; employee_id?: string; q?: string; active?: 'active' | 'inactive'; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<CivilLawContract>>('/civil-law-contracts', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CivilLawContract>(`/civil-law-contracts/${id}`).then((r) => r.data),
  create: (p: CivilLawContractCreate) =>
    api.post<CivilLawContract>('/civil-law-contracts', p).then((r) => r.data),
  update: (id: string, p: Partial<CivilLawContractCreate>) =>
    api.patch<CivilLawContract>(`/civil-law-contracts/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/civil-law-contracts/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<CivilLawContract>(`/civil-law-contracts/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<CivilLawContract>(`/civil-law-contracts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, cancellation_reason: string) =>
    api.post(`/civil-law-contracts/${id}/cancel`, { cancellation_reason }).then((r) => r.data),
};

export interface StaffTransfer {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  transfer_date: string;
  new_position_id: string | null;
  new_department_id: string | null;
  employment_kind: EmploymentKind | null;
  new_salary_amount: string;
  new_calc_type_id: string | null;
  reason: string | null;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  external_source: string | null;
  planned_accruals?: PlannedAccrualLine[];
}

export interface StaffTransferCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  transfer_date: string;
  new_position_id?: string | null;
  new_department_id?: string | null;
  employment_kind?: EmploymentKind | null;
  new_salary_amount: string;
  new_calc_type_id?: string | null;
  reason?: string | null;
  comment?: string | null;
}

// Повідомлення про прийняття працівника на роботу (КМУ №413, е-форма J3001005).
// Друкована форма + експорт XML для подання через Е-кабінет / M.E.Doc.
export interface HireNotificationItem {
  id?: string;
  line_number?: number;
  employee_id: string | null;
  category: number;          // 1 осн.місце / 2 сумісництво / 3 гіг / 4 ЦПД / 5 домашній
  tax_number: string | null;
  unzr: string | null;
  last_name: string;
  first_name: string;
  middle_name: string | null;
  citizenship: number;       // 0 іноземець / 1 громадянин України
  order_number: string | null;
  order_date: string;
  start_date: string;
  hire_order_id?: string | null;
  civil_contract_id?: string | null;
}

export interface HireNotification {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  notification_type: 'INITIAL' | 'CANCELING';
  c_reg: number | null;
  c_raj: number | null;
  c_sti_orig: number | null;
  doc_cnt: number;
  fill_date: string | null;
  kbos_inn: string | null;
  bos_name: string | null;
  kbuh_inn: string | null;
  buh_name: string | null;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  items: HireNotificationItem[];
}

export interface HireNotificationCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  notification_type?: 'INITIAL' | 'CANCELING';
  c_reg?: number | null;
  c_raj?: number | null;
  c_sti_orig?: number | null;
  doc_cnt?: number;
  fill_date?: string | null;
  kbos_inn?: string | null;
  bos_name?: string | null;
  kbuh_inn?: string | null;
  buh_name?: string | null;
  comment?: string | null;
  items?: HireNotificationItem[];
}

export interface HireNotificationCandidate {
  source_type: 'hire_order' | 'civil_contract';
  source_id: string;
  source_number: string;
  employee_id: string;
  category: number;
  tax_number: string | null;
  last_name: string;
  first_name: string;
  middle_name: string | null;
  citizenship: number;
  order_number: string | null;
  order_date: string;
  start_date: string;
  already_notified: boolean;
}

export const hireNotifications = {
  list: (params?: { organization_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<HireNotification>>('/hire-notifications', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<HireNotification>(`/hire-notifications/${id}`).then((r) => r.data),
  candidates: (params: { organization_id: string; date_from?: string; date_to?: string }) =>
    api.get<HireNotificationCandidate[]>('/hire-notifications/candidates', { params })
      .then((r) => r.data),
  create: (p: HireNotificationCreate) =>
    api.post<HireNotification>('/hire-notifications', p).then((r) => r.data),
  update: (id: string, p: Partial<HireNotificationCreate>) =>
    api.patch<HireNotification>(`/hire-notifications/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`/hire-notifications/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<HireNotification>(`/hire-notifications/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<HireNotification>(`/hire-notifications/${id}/unpost`).then((r) => r.data),
  // Експорт XML (blob — JWT їде у header; ім'я файлу — з Content-Disposition)
  xml: (id: string) =>
    api.get(`/hire-notifications/${id}/xml`, { responseType: 'blob' }),
};

export const staffTransfers = {
  list: (params?: { organization_id?: string; employee_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<StaffTransfer>>('/staff-transfers', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<StaffTransfer>(`/staff-transfers/${id}`).then((r) => r.data),
  create: (p: StaffTransferCreate) =>
    api.post<StaffTransfer>('/staff-transfers', p).then((r) => r.data),
  update: (id: string, p: Partial<StaffTransferCreate>) =>
    api.patch<StaffTransfer>(`/staff-transfers/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/staff-transfers/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<StaffTransfer>(`/staff-transfers/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<StaffTransfer>(`/staff-transfers/${id}/unpost`).then((r) => r.data),
};

export interface DismissalOrder {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  dismiss_date: string;
  reason_article: string | null;
  reason: string | null;
  compensation_amount: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  external_source: string | null;
  // Block 5.3f:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface DismissalOrderCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  dismiss_date: string;
  reason_article?: string | null;
  reason?: string | null;
  compensation_amount?: string;
  comment?: string | null;
}

export const dismissalOrders = {
  list: (params?: { organization_id?: string; employee_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<DismissalOrder>>('/dismissal-orders', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<DismissalOrder>(`/dismissal-orders/${id}`).then((r) => r.data),
  create: (p: DismissalOrderCreate) =>
    api.post<DismissalOrder>('/dismissal-orders', p).then((r) => r.data),
  update: (id: string, p: Partial<DismissalOrderCreate>) =>
    api.patch<DismissalOrder>(`/dismissal-orders/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/dismissal-orders/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<DismissalOrder>(`/dismissal-orders/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<DismissalOrder>(`/dismissal-orders/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/dismissal-orders/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ─────────────── Планові нарахування (фіча картки #3) ───────────────

export interface AccrualType {
  id: string;
  code: string;
  name: string;
  value_kind: 'FIXED_SUM' | 'PERCENT' | 'RATE';
  is_base_salary: boolean;
  is_base_for_percent: boolean;
  is_time_proportional: boolean;
  calc_order: number;
  category: 'BASE' | 'SUPPLEMENT' | 'PREMIUM' | 'INDEX';
  is_active: boolean;
  is_seed: boolean;
  sort_order: number;
}

// Рішення власника: показуємо лише 4 системних види + усі імпортовані/користувацькі
// (is_seed=false). Решта системних (відрядна, надбавки %, доплати за умови тощо) —
// приховані до реалізації рушіїв (TECHDEBT TD-ACCRUAL-TYPES-HIDDEN).
const VISIBLE_SEED_ACCRUAL_CODES = new Set([
  'SUPPLEMENT_FIXED',  // Доплата (фіксована)
  'SALARY',            // Оклад (місячна ставка)
  'HOURLY',            // Погодинна оплата
  'INDEXATION',        // Індексація
]);

export function visibleAccrualTypes(types: AccrualType[] | undefined): AccrualType[] {
  return (types ?? []).filter(
    (t) => !t.is_seed || VISIBLE_SEED_ACCRUAL_CODES.has(t.code),
  );
}

export interface AccrualTypeCreate {
  code: string;
  name: string;
  value_kind?: 'FIXED_SUM' | 'PERCENT' | 'RATE';
  is_base_salary?: boolean;
  is_base_for_percent?: boolean;
  is_time_proportional?: boolean;
  calc_order?: number;
  category?: 'BASE' | 'SUPPLEMENT' | 'PREMIUM' | 'INDEX';
  is_active?: boolean;
  sort_order?: number;
}

export const VALUE_KIND_LABELS: Record<string, string> = {
  FIXED_SUM: 'Фіксована сума',
  PERCENT: 'Відсоток (%)',
  RATE: 'Тариф × кількість',
};

export const accrualTypes = {
  list: () => api.get<AccrualType[]>('/refs/accrual-types').then((r) => r.data),
  create: (p: AccrualTypeCreate) =>
    api.post<AccrualType>('/refs/accrual-types', p).then((r) => r.data),
  update: (id: string, p: Partial<AccrualTypeCreate>) =>
    api.patch<AccrualType>(`/refs/accrual-types/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/refs/accrual-types/${id}`).then(() => undefined),
};

// Документ «Введення/зміна планових нарахувань».
export interface PlannedAccrualItem {
  id?: string;
  line_number?: number;
  employee_id: string;
  employee_name?: string | null;  // ПIБ (резолв на backend для списку)
  accrual_type_id: string;
  action: 'ADD' | 'CHANGE' | 'CLOSE';
  value_kind?: 'FIXED_SUM' | 'PERCENT' | 'RATE' | null;
  value?: string | null;
  percent_base_accrual_type_id?: string | null;
  rate_quantity?: string | null;
}

export interface PlannedAccrualOrder {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  effective_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  external_source?: string | null;  // значок «І» імпорту з 1С
  items: PlannedAccrualItem[];
}

export interface PlannedAccrualOrderCreate {
  organization_id: string;
  document_number?: string;
  document_date: string;
  effective_date: string;
  comment?: string | null;
  items: PlannedAccrualItem[];
}

export const plannedAccrualOrders = {
  list: (params?: { organization_id?: string; employee_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<PlannedAccrualOrder>>('/planned-accrual-orders', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<PlannedAccrualOrder>(`/planned-accrual-orders/${id}`).then((r) => r.data),
  create: (p: PlannedAccrualOrderCreate) =>
    api.post<PlannedAccrualOrder>('/planned-accrual-orders', p).then((r) => r.data),
  update: (id: string, p: Partial<PlannedAccrualOrderCreate>) =>
    api.patch<PlannedAccrualOrder>(`/planned-accrual-orders/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/planned-accrual-orders/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<PlannedAccrualOrder>(`/planned-accrual-orders/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<PlannedAccrualOrder>(`/planned-accrual-orders/${id}/unpost`).then((r) => r.data),
};

// ─────────── Штатний розклад (doc.staff_schedules, Phase-3.2.5 + зріз) ───────────

export interface StaffScheduleItem {
  line_number: number;
  department_id: string | null;  // nullable (alembic 0305) — штатне без підрозділу
  position_id: string;
  rate_count: string;   // Decimal as string
  salary: string;
  calc_type_id: string | null;   // вид розрахунку штатної одиниці (alembic 0306)
  comment: string | null;
}

export interface StaffSchedule {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  kind: 'INITIAL' | 'CHANGE';
  approved_at: string | null;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  state: string;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  items: StaffScheduleItem[];
}

export interface StaffScheduleCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  kind: 'INITIAL' | 'CHANGE';
  approved_at?: string | null;
  comment?: string | null;
  items: StaffScheduleItem[];
}

// Рядок зрізу штатного розкладу на дату (з діючих працівників).
export interface StaffScheduleSnapshotItem {
  department_id: string | null;
  position_id: string | null;
  rate_count: string;             // group_by=position → к-сть людей; employee → 1
  salary: string;                 // оклад (SALARY) АБО годинна ставка (HOURLY)
  monthly_fot?: string;           // місячний ФОТ: HOURLY → ставка×норма_годин, інакше оклад
  calc_type_code: string | null;  // вид розрахунку (SALARY/HOURLY/...)
  employee_id: string | null;     // лише group_by='employee'
  employee_name: string | null;
}

// Розкладка окладу штатного розкладу по працівниках (для наказу зміни нарахувань).
export interface StaffScheduleEmployeeRow {
  employee_id: string;
  employee_name: string;
  department_id: string;
  position_id: string;
  salary: string;
}

const SS_BASE = '/documents/staff-schedules';
export interface StaffScheduleListRow {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  kind: 'INITIAL' | 'CHANGE';
  posted: boolean;
  deletion_mark: boolean;
  items_count: number;
  external_source?: string | null;
}

export const staffSchedules = {
  // Серверна пагінація/сортування (ag-Grid Infinite, 2026-08-23).
  page: (params: {
    organization_id: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<Page<StaffScheduleListRow>>(`${SS_BASE}/page`, { params }).then((r) => r.data),
  list: (organization_id: string) =>
    api.get<StaffSchedule[]>(SS_BASE, { params: { organization_id } }).then((r) => r.data),
  get: (id: string) =>
    api.get<StaffSchedule>(`${SS_BASE}/${id}`).then((r) => r.data),
  create: (p: StaffScheduleCreate) =>
    api.post<StaffSchedule>(SS_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<StaffScheduleCreate>) =>
    api.patch<StaffSchedule>(`${SS_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${SS_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<StaffSchedule>(`${SS_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<StaffSchedule>(`${SS_BASE}/${id}/unpost`).then((r) => r.data),
  // Зріз штатного розкладу на дату (з діючих працівників). group_by: employee|position.
  snapshot: (organization_id: string, on_date: string, group_by: 'employee' | 'position' = 'employee') =>
    api.get<StaffScheduleSnapshotItem[]>(`${SS_BASE}/snapshot`,
      { params: { organization_id, on_date, group_by } }).then((r) => r.data),
  // Розкладка окладів документа по активних працівниках (для наказу зміни нарахувань).
  resolveEmployees: (id: string) =>
    api.get<StaffScheduleEmployeeRow[]>(`${SS_BASE}/${id}/resolve-employees`).then((r) => r.data),
};
